// Mercapoli - App Principal
// Preparada para Web, iOS (WebView/Capacitor), Android
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Contexts
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CityProvider } from "./context/CityContext";

// Pages - Public
import Landing from "./pages/Landing";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import RegistroComercio from "./pages/RegistroComercio";
import RegistroRider from "./pages/RegistroRider";

// Pages - Legal (Required for App Store / Play Store)
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsConditions from "./pages/legal/TermsConditions";
import DeleteAccount from "./pages/legal/DeleteAccount";

// Pages - Corporate
import SobreNosotros from "./pages/corporate/SobreNosotros";
import TrabajaConNosotros from "./pages/corporate/TrabajaConNosotros";
import Blog from "./pages/corporate/Blog";
import TerminosCondicionesCorp from "./pages/corporate/TerminosCondiciones";
import PoliticaPrivacidadCorp from "./pages/corporate/PoliticaPrivacidad";
import PoliticaCookies from "./pages/corporate/PoliticaCookies";

// Pages - Customer (/app)
import CustomerHome from "./pages/customer/Home";
import RestaurantDetail from "./pages/customer/RestaurantDetail";
import Cart from "./pages/customer/Cart";
import Checkout from "./pages/customer/Checkout";
import PaymentSuccess from "./pages/customer/PaymentSuccess";
import OrderTracking from "./pages/customer/OrderTracking";
import CustomerOrders from "./pages/customer/Orders";
import CustomerProfile from "./pages/customer/Profile";
import Favorites from "./pages/customer/Favorites";
import ExplorePage from "./pages/customer/Explore";

// Pages - Rider (/rider)
import DeliveryDashboard from "./pages/delivery/Dashboard";
import DeliveryOrders from "./pages/delivery/Orders";
import DeliveryEarnings from "./pages/delivery/Earnings";

// Pages - Merchant/Comercio (/merchant)
import MerchantDashboard from "./pages/merchant/Dashboard";
import MerchantProductos from "./pages/merchant/Productos";
import MerchantPromociones from "./pages/merchant/Promociones";
import MerchantReviews from "./pages/merchant/Reviews";
import MerchantVentas from "./pages/merchant/Ventas";
import MerchantLiquidaciones from "./pages/merchant/Liquidaciones";
import MerchantConfiguracion from "./pages/merchant/Configuracion";
import MerchantMenuWidget from "./pages/merchant/MenuWidget";
import RestaurantMenu from "./pages/restaurant/Menu";
import RestaurantOrders from "./pages/restaurant/Orders";

// Pages - Public Menu (Embebible)
import PublicMenu from "./pages/PublicMenu";

// Pages - Admin
import AdminDashboard from "./pages/admin/Dashboard";
import AdminUsers from "./pages/admin/Users";
import AdminRestaurants from "./pages/admin/Restaurants";
import AdminOrders from "./pages/admin/Orders";
import AdminCategories from "./pages/admin/Categories";
import AdminCupones from "./pages/admin/Cupones";
import AdminTiposComercio from "./pages/admin/TiposComercio";
import AdminSolicitudes from "./pages/admin/Solicitudes";
import AdminRepartidores from "./pages/admin/Repartidores";
import AdminFinanzas from "./pages/admin/Finanzas";
import AdminDiggii from "./pages/admin/Diggii";
import RestaurantForm from "./pages/admin/RestaurantForm";
import RepartidorForm from "./pages/admin/RepartidorForm";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import NativeAppWrapper from "./components/NativeAppWrapper";

import "./App.css";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <CityProvider>
            <NativeAppWrapper>
              <BrowserRouter>
                <Routes>
                  {/* ==================== PÁGINAS PÚBLICAS ==================== */}
                <Route path="/" element={<Landing />} />
                <Route path="/ciudad/:ciudadSlug" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/recuperar-password" element={<ForgotPassword />} />
                <Route path="/registro-comercio" element={<RegistroComercio />} />
                <Route path="/registro-rider" element={<RegistroRider />} />
                
                {/* Menú Público Embebible (Fase 4.1) */}
                <Route path="/menu/:merchantId" element={<PublicMenu />} />
                
              {/* Páginas Legales (Requeridas para App Store / Play Store) */}
              <Route path="/privacidad" element={<PrivacyPolicy />} />
              <Route path="/terminos" element={<TermsConditions />} />
              <Route path="/eliminar-cuenta" element={
                <ProtectedRoute roles={["customer", "delivery", "restaurant"]}>
                  <DeleteAccount />
                </ProtectedRoute>
              } />

              {/* Páginas Corporativas */}
              <Route path="/sobre-nosotros" element={<SobreNosotros />} />
              <Route path="/trabaja-con-nosotros" element={<TrabajaConNosotros />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/terminos-y-condiciones" element={<TerminosCondicionesCorp />} />
              <Route path="/politica-de-privacidad" element={<PoliticaPrivacidadCorp />} />
              <Route path="/politica-de-cookies" element={<PoliticaCookies />} />

              {/* ==================== CUSTOMER APP (/app) ==================== */}
              <Route path="/app" element={
                <ProtectedRoute roles={["customer"]}>
                  <CustomerHome />
                </ProtectedRoute>
              } />
              <Route path="/app/restaurante/:id" element={
                <ProtectedRoute roles={["customer"]}>
                  <RestaurantDetail />
                </ProtectedRoute>
              } />
              <Route path="/app/carrito" element={
                <ProtectedRoute roles={["customer"]}>
                  <Cart />
                </ProtectedRoute>
              } />
              <Route path="/app/checkout" element={
                <ProtectedRoute roles={["customer"]}>
                  <Checkout />
                </ProtectedRoute>
              } />
              <Route path="/app/pago-exitoso" element={
                <ProtectedRoute roles={["customer"]}>
                  <PaymentSuccess />
                </ProtectedRoute>
              } />
              <Route path="/app/pedido/:id" element={
                <ProtectedRoute roles={["customer"]}>
                  <OrderTracking />
                </ProtectedRoute>
              } />
              <Route path="/app/pedidos" element={
                <ProtectedRoute roles={["customer"]}>
                  <CustomerOrders />
                </ProtectedRoute>
              } />
              <Route path="/app/perfil" element={
                <ProtectedRoute roles={["customer"]}>
                  <CustomerProfile />
                </ProtectedRoute>
              } />
              <Route path="/app/favoritos" element={
                <ProtectedRoute roles={["customer"]}>
                  <Favorites />
                </ProtectedRoute>
              } />
              <Route path="/app/explorar" element={
                <ProtectedRoute roles={["customer"]}>
                  <ExplorePage />
                </ProtectedRoute>
              } />

              {/* ==================== RIDER APP (/rider) ==================== */}
              <Route path="/rider" element={
                <ProtectedRoute roles={["delivery"]}>
                  <DeliveryDashboard />
                </ProtectedRoute>
              } />
              <Route path="/rider/pedidos" element={
                <ProtectedRoute roles={["delivery"]}>
                  <DeliveryOrders />
                </ProtectedRoute>
              } />
              <Route path="/rider/ganancias" element={
                <ProtectedRoute roles={["delivery"]}>
                  <DeliveryEarnings />
                </ProtectedRoute>
              } />
              {/* Legacy routes redirect */}
              <Route path="/delivery" element={<Navigate to="/rider" replace />} />
              <Route path="/delivery/*" element={<Navigate to="/rider" replace />} />

              {/* ==================== MERCHANT APP (/merchant) ==================== */}
              <Route path="/merchant" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <MerchantDashboard />
                </ProtectedRoute>
              } />
              <Route path="/merchant/productos" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <MerchantProductos />
                </ProtectedRoute>
              } />
              <Route path="/merchant/promociones" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <MerchantPromociones />
                </ProtectedRoute>
              } />
              <Route path="/merchant/reviews" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <MerchantReviews />
                </ProtectedRoute>
              } />
              <Route path="/merchant/ventas" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <MerchantVentas />
                </ProtectedRoute>
              } />
              <Route path="/merchant/liquidaciones" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <MerchantLiquidaciones />
                </ProtectedRoute>
              } />
              <Route path="/merchant/configuracion" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <MerchantConfiguracion />
                </ProtectedRoute>
              } />
              <Route path="/merchant/menu" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <RestaurantMenu />
                </ProtectedRoute>
              } />
              <Route path="/merchant/pedidos" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <RestaurantOrders />
                </ProtectedRoute>
              } />
              <Route path="/merchant/menu-widget" element={
                <ProtectedRoute roles={["restaurant"]}>
                  <MerchantMenuWidget />
                </ProtectedRoute>
              } />
              {/* Legacy routes redirect */}
              <Route path="/restaurant" element={<Navigate to="/merchant" replace />} />
              <Route path="/restaurant/*" element={<Navigate to="/merchant" replace />} />

              {/* ==================== ADMIN PANEL (/admin) ==================== */}
              <Route path="/admin" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/usuarios" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminUsers />
                </ProtectedRoute>
              } />
              <Route path="/admin/restaurantes" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminRestaurants />
                </ProtectedRoute>
              } />
              <Route path="/admin/pedidos" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminOrders />
                </ProtectedRoute>
              } />
              <Route path="/admin/categorias" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminCategories />
                </ProtectedRoute>
              } />
              <Route path="/admin/cupones" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminCupones />
                </ProtectedRoute>
              } />
              <Route path="/admin/tipos-comercio" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminTiposComercio />
                </ProtectedRoute>
              } />
              <Route path="/admin/solicitudes" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminSolicitudes />
                </ProtectedRoute>
              } />
              <Route path="/admin/repartidores" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminRepartidores />
                </ProtectedRoute>
              } />
              <Route path="/admin/repartidores/nuevo" element={
                <ProtectedRoute roles={["admin"]}>
                  <RepartidorForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/repartidores/editar/:id" element={
                <ProtectedRoute roles={["admin"]}>
                  <RepartidorForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/finanzas" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminFinanzas />
                </ProtectedRoute>
              } />
              <Route path="/admin/diggii" element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDiggii />
                </ProtectedRoute>
              } />
              <Route path="/admin/restaurantes/nuevo" element={
                <ProtectedRoute roles={["admin"]}>
                  <RestaurantForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/restaurantes/editar/:id" element={
                <ProtectedRoute roles={["admin"]}>
                  <RestaurantForm />
                </ProtectedRoute>
              } />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'DM Sans, sans-serif',
              },
            }}
          />
            </NativeAppWrapper>
          </CityProvider>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
